import React from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import swal from 'sweetalert';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Download,
  Expand,
  FileText,
  Filter,
  Lock,
  PlusCircle,
  Plus,
  RefreshCw,
  Search,
  Shrink,
  SlidersHorizontal,
  Wallet as WalletIcon,
  X,
} from 'lucide-react';

import '../../../../styles/components/wallet-summary.scss';
import i18n from '../../../../i18n';
import { getLoggedUser } from '../../../../state/user';
import { getAllOrganization } from '../../../../models/organization';
import { getGroupsList } from '../../../../models/groups';
import { getUserInfo } from '../../../../models/users';
import { createWallet, getAllWallets } from '../../../../models/wallet';
import { SideBar } from '../../sidebar';
import HeaderBanner from '../../../common/HeaderBanner';
import { TRANSACTION_TYPES, USER_TRANSACTION_STATUS } from '../../../../constants/status';

const user = getLoggedUser();

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const t = (key, fallback) => {
  const value = i18n.t(key);
  return value && value !== key ? value : fallback || key;
};

const ROWS_PER_PAGE_OPTIONS = [10, 15, 25, 50];
const DEFAULT_LIST = { page: 1, limit: 10 };

// Transaction-type filter values. The summary cards and the "All Types" dropdown
// both set `trans_type`, so they always stay in sync.
// NOTE: 'client_due' is new — make sure the wallets API accepts it for trans_type.
const TYPE_FILTERS = {
  deposit: 'Deposit',
  client_due: 'Client Due',
  hold: 'Hold',
  withdraw: 'Withdraw',
};

// Filters kept in the URL so they survive a refresh / can be shared as a link.
const URL_FILTER_KEYS = ['search', 'trans_type', 'pay_type', 'client', 'group', 'fdate', 'tdate'];
const ADVANCED_FILTER_KEYS = ['pay_type', 'client', 'group', 'fdate', 'tdate'];

// Read page, limit and filters from the current URL (invalid values fall back to defaults).
const readListFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const list = { ...DEFAULT_LIST };

  URL_FILTER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) list[key] = value;
  });

  const page = parseInt(params.get('page'), 10);
  if (page > 0) list.page = page;

  const limit = parseInt(params.get('limit'), 10);
  if (ROWS_PER_PAGE_OPTIONS.includes(limit)) list.limit = limit;

  return list;
};

// Write the list to the URL without reloading or adding history entries.
// Other query params already in the URL are left untouched; defaults are omitted.
const writeListToUrl = (list) => {
  const params = new URLSearchParams(window.location.search);

  URL_FILTER_KEYS.forEach((key) => {
    if (list[key]) params.set(key, list[key]);
    else params.delete(key);
  });

  if (list.page && list.page !== DEFAULT_LIST.page) params.set('page', list.page);
  else params.delete('page');

  if (list.limit && list.limit !== DEFAULT_LIST.limit) params.set('limit', list.limit);
  else params.delete('limit');

  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  );
};

const toNumber = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v) =>
  toNumber(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (value) => {
  if (!value) return '—';
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(String(value));
  if (iso) return iso[0];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const timeAgo = (date) => {
  if (!date) return '';
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return t('just_now', 'just now');
  if (mins < 60) return `${mins} ${t('min_ago', 'min ago')}`;
  const hours = Math.floor(mins / 60);
  return `${hours} ${t('hours_ago', 'h ago')}`;
};

const typeLabel = (row) => TRANSACTION_TYPES[row.type]?.text || row.type || '—';
const statusLabel = (row) =>
  row.status ? USER_TRANSACTION_STATUS[row.status]?.text || row.status : '—';

// Row icon + colour, picked from the transaction type (falls back to credit/debit).
const typeVisual = (row) => {
  const key = `${row.type || ''} ${typeLabel(row)}`.toLowerCase();
  if (key.includes('enrol')) return { Icon: FileText, tone: 'teal' };
  if (key.includes('withdraw')) return { Icon: ArrowUpRight, tone: 'blue' };
  if (key.includes('hold')) return { Icon: Lock, tone: 'red' };
  if (key.includes('due') || key.includes('client')) return { Icon: Building2, tone: 'amber' };
  if (toNumber(row.credit) > 0) return { Icon: PlusCircle, tone: 'green' };
  return { Icon: FileText, tone: 'teal' };
};

// Status pill colour from the status text.
const statusTone = (row) => {
  const s = statusLabel(row).toLowerCase();
  if (s.includes('request') || s.includes('process')) return 'info';
  if (s.includes('approv') || s.includes('success') || s.includes('complete') || s.includes('paid'))
    return 'success';
  if (s.includes('reject') || s.includes('fail') || s.includes('cancel') || s.includes('declin'))
    return 'danger';
  if (s.includes('pend') || s.includes('hold') || s.includes('wait')) return 'warning';
  return 'neutral';
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export class WalletSummaryNew extends React.Component {
  constructor() {
    super();
    const initialList = readListFromUrl();

    this.state = {
      hideSidebar: false,

      // wallet
      wallet_loading: false,
      user_info: {},
      last_updated: null,

      // transactions
      is_loading: false,
      transactions: [],
      totalRows: 0,
      credit_sum: 0,
      debit_sum: 0,
      balance_sum: 0,
      hold_sum: 0,
      client_due: 0,
      list: initialList,
      // Open the filter panel if the URL already has advanced filters in it.
      show_filters: ADVANCED_FILTER_KEYS.some((k) => initialList[k]),
      exporting: false,

      // filter options
      clients: [],
      groups: [],

      // withdraw
      withdraw_popup: false,
      input: {},
      errors: {},
      is_wthdraw_req_sending: false,
    };
    this.searchTimer = null;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.loadAllOrganisation();
    this.loadAllGroups();
    this.checkUserWallet();
    this.loadAllTransactions();
    // Keeps the "Updated x min ago" label fresh.
    this.tick = setInterval(() => this.forceUpdate(), 30000);
  }

  componentWillUnmount() {
    clearInterval(this.tick);
    clearTimeout(this.searchTimer);
  }

  /* --------------------------------- Data --------------------------------- */

  buildQuery(overrides = {}) {
    const list = { ...this.state.list, ...overrides };
    const params = Object.fromEntries(
      Object.entries(list).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
    return `customer=${user.id}&${new URLSearchParams(params).toString()}`;
  }

  async loadAllOrganisation() {
    try {
      const response = await getAllOrganization();
      this.setState({ clients: (response && response.data) || [] });
    } catch (error) {
      console.error(error);
    }
  }

  async loadAllGroups() {
    try {
      const { list } = this.state;
      const q = list.client ? `organization=${list.client}` : '';
      const response = await getGroupsList(q);
      this.setState({ groups: (response && response.data) || [] });
    } catch (error) {
      console.error(error);
    }
  }

  async checkUserWallet() {
    this.setState({ wallet_loading: true });
    try {
      const response = await getUserInfo(user.id);
      if (response && response.success) {
        const data = response.data;
        this.setState({
          user_info: data,
          last_updated: new Date(),
          input: {
            bank: data.bank || '',
            beneficiary: data.beneficiary || '',
            account_number: data.account_number || '',
            amount: data.wallet_amount || '',
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ wallet_loading: false });
    }
  }

  async loadAllTransactions() {
    this.setState({ is_loading: true });
    try {
      const response = await getAllWallets(this.buildQuery());
      if (response && response.success) {
        const meta = response.meta || {};

        // A page from the URL can be past the end (e.g. rows were removed since
        // the link was saved) → jump to the last page that exists.
        const total = meta.total || 0;
        const lastPage = Math.max(1, Math.ceil(total / this.state.list.limit));
        if (this.state.list.page > lastPage) {
          this.setListAndReload({ page: lastPage });
          return;
        }

        this.setState({
          transactions: response.data || [],
          totalRows: meta.total || 0,
          credit_sum: meta.credit_sum || 0,
          debit_sum: meta.debit_sum || 0,
          balance_sum: meta.balance_sum || 0,
          hold_sum: meta.hold_sum || 0,
          client_due: meta.client_due || 0,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ is_loading: false });
    }
  }

  refreshAll = () => {
    this.checkUserWallet();
    this.loadAllTransactions();
  };

  /* ------------------------------- Filtering ------------------------------ */

  setListAndReload = (changes, { debounce = false, reloadGroups = false } = {}) => {
    this.setState(
      (prev) => ({ list: { ...prev.list, ...changes } }),
      () => {
        writeListToUrl(this.state.list); // keep filters in the URL
        if (reloadGroups) this.loadAllGroups();
        clearTimeout(this.searchTimer);
        if (debounce) {
          this.searchTimer = setTimeout(() => this.loadAllTransactions(), 400);
        } else {
          this.loadAllTransactions();
        }
      }
    );
  };

  handleSearchChange = (event) => {
    const { name, value } = event.target;
    // Any filter change goes back to page 1.
    this.setListAndReload(
      { [name]: value, page: 1, ...(name === 'client' ? { group: '' } : {}) },
      { debounce: name === 'search', reloadGroups: name === 'client' }
    );
  };

  clearFilters = () => {
    this.setListAndReload(
      {
        search: '',
        trans_type: '',
        pay_type: '',
        client: '',
        group: '',
        fdate: '',
        tdate: '',
        page: 1,
      },
      { reloadGroups: true }
    );
  };

  activeFilterCount() {
    const { list } = this.state;
    return ADVANCED_FILTER_KEYS.filter((k) => list[k]).length;
  }

  hasAnyFilter() {
    return URL_FILTER_KEYS.some((k) => this.state.list[k]);
  }

  // Summary card click: filter by that type, click again to remove the filter.
  handleStatClick = (type) => {
    const current = this.state.list.trans_type;
    this.setListAndReload({ trans_type: current === type ? '' : type, page: 1 });
  };

  typeFilterLabel(type) {
    return t(type, TYPE_FILTERS[type] || type);
  }

  /* ------------------------------ Pagination ------------------------------ */

  goToPage = (page) => {
    const totalPages = this.getTotalPages();
    const next = Math.min(Math.max(1, page), totalPages);
    if (next === this.state.list.page) return;
    this.setListAndReload({ page: next });
  };

  handleLimitChange = (e) => {
    this.setListAndReload({ limit: Number(e.target.value), page: 1 });
  };

  getTotalPages() {
    const { totalRows, list } = this.state;
    return Math.max(1, Math.ceil(totalRows / list.limit));
  }

  /* -------------------------------- Export -------------------------------- */

  handleExport = async () => {
    const { totalRows, transactions } = this.state;
    this.setState({ exporting: true });

    let rows = transactions;
    try {
      // Pull every row that matches the current filters, not just this page.
      if (totalRows > transactions.length) {
        const response = await getAllWallets(this.buildQuery({ page: 1, limit: totalRows }));
        if (response && response.success && Array.isArray(response.data)) rows = response.data;
      }
    } catch (error) {
      console.error(error);
    }

    const header = ['ID', 'Transaction', 'Reference', 'Credit', 'Debit', 'Type', 'Status', 'Date'];
    const lines = rows.map((r) =>
      [
        r.id,
        r.transaction_id,
        r.reference,
        toNumber(r.credit) ? toNumber(r.credit).toFixed(2) : '',
        toNumber(r.debit) ? toNumber(r.debit).toFixed(2) : '',
        typeLabel(r),
        statusLabel(r),
        formatDate(r.created_at),
      ]
        .map(csvCell)
        .join(',')
    );

    const blob = new Blob([`﻿${[header.map(csvCell).join(','), ...lines].join('\n')}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-transactions-${formatDate(new Date())}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    this.setState({ exporting: false });
  };

  /* -------------------------------- Withdraw ------------------------------ */

  openWithdraw = () => {
    const { user_info } = this.state;
    if (toNumber(user_info.wallet_amount) < 1) {
      swal(t('Warning'), t('insufficient_balance', 'Insufficient balance to withdraw.'), 'warning');
      return;
    }
    this.setState((prev) => ({
      withdraw_popup: true,
      errors: {},
      input: {
        ...prev.input,
        bank: prev.input.bank ?? user_info.bank ?? '',
        beneficiary: prev.input.beneficiary ?? user_info.beneficiary ?? '',
        account_number: prev.input.account_number ?? user_info.account_number ?? '',
        amount: user_info.wallet_amount || '',
      },
    }));
  };

  closeWithdraw = () => {
    if (!this.state.is_wthdraw_req_sending) this.setState({ withdraw_popup: false });
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    const { user_info } = this.state;

    if (name === 'amount') {
      if (value !== '' && !/^\d*\.?\d{0,3}$/.test(value)) return; // numbers only
      if (toNumber(value) > toNumber(user_info.wallet_amount)) {
        this.setState((prev) => ({
          errors: {
            ...prev.errors,
            amount: `${t('amount_exceeds_balance', 'Amount should not exceed')} ${fmt(
              user_info.wallet_amount
            )}`,
          },
        }));
        return;
      }
    }

    this.setState((prev) => ({
      input: { ...prev.input, [name]: value },
      errors: { ...prev.errors, [name]: undefined },
    }));
  };

  setMaxAmount = () => {
    const { user_info } = this.state;
    this.setState((prev) => ({
      input: { ...prev.input, amount: String(user_info.wallet_amount || '') },
      errors: { ...prev.errors, amount: undefined },
    }));
  };

  validate() {
    const { input, user_info } = this.state;
    const errors = {};

    if (!String(input.bank || '').trim()) errors.bank = t('bank_required', 'Bank is required');
    if (!String(input.beneficiary || '').trim())
      errors.beneficiary = t('beneficiary_required', 'Beneficiary is required');
    if (!String(input.account_number || '').trim())
      errors.account_number = t('account_number_required', 'Account number is required');

    if (!input.amount) {
      errors.amount = t('amount_required', 'Amount is required');
    } else if (toNumber(input.amount) <= 0) {
      errors.amount = t('amount_invalid', 'Enter a valid amount');
    } else if (toNumber(input.amount) > toNumber(user_info.wallet_amount)) {
      errors.amount = t('amount_exceeds_balance', 'Amount should not exceed the balance');
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.validate()) return;

    this.setState({ is_wthdraw_req_sending: true });
    const { input } = this.state;

    try {
      const payload = {
        no_balance_update: false,
        user: user.id,
        type: 'withdraw',
        debit: input.amount,
        credit: 0,
        status: 'W',
        method: 'offline',
        bank: input.bank,
        beneficiary: input.beneficiary,
        account_number: input.account_number,
      };

      const response = await createWallet(payload);

      if (response && response.success) {
        this.setState({ withdraw_popup: false, errors: {} });
        swal(t('success', 'Success'), t('withdraw_display_message'), 'success', {
          buttons: false,
          timer: 10000,
        });
        this.refreshAll();
      } else {
        let message = (response && response.message) || 'Something went wrong';
        if (response && response.data && typeof response.data === 'object') {
          Object.values(response.data).forEach((v) => {
            message += `\n${v}`;
          });
        }
        swal('Failed', message, 'error');
      }
    } catch (err) {
      console.error(err);
      swal('Error', 'An unexpected error occurred', 'error');
    } finally {
      this.setState({ is_wthdraw_req_sending: false });
    }
  };

  /* --------------------------------- Render ------------------------------- */

  renderBalanceCard() {
    const { user_info, wallet_loading, last_updated } = this.state;
    const canWithdraw = toNumber(user_info.wallet_amount) >= 1;

    return (
      <div className="wlt-balance">
        <div className="wlt-balance__label">
          {t('available_balance', 'Available Balance')}
          <button
            type="button"
            className={`wlt-balance__refresh ${wallet_loading ? 'is-spinning' : ''}`}
            onClick={this.refreshAll}
            aria-label={t('refresh', 'Refresh')}
          >
            <RefreshCw size={12} />
          </button>
        </div>

        <div className="wlt-balance__amount">
          <span className="wlt-num">{fmt(user_info.wallet_amount)}</span>
          <span className="wlt-balance__currency">{t('omr', 'OMR')}</span>
        </div>

        <div className="wlt-balance__updated">
          <Clock size={13} />
          {last_updated
            ? `${t('updated', 'Updated')} ${timeAgo(last_updated)}`
            : t('loading', 'Loading…')}
        </div>

        <div className="wlt-balance__actions">
          <Link to="/wallet-manage" className="wlt-btn wlt-btn--lime">
            <Plus size={16} />
            {t('add_fund', 'Add Funds')}
          </Link>
          <button
            type="button"
            className="wlt-btn wlt-btn--glass"
            onClick={this.openWithdraw}
            disabled={!canWithdraw}
          >
            <ArrowUpRight size={16} />
            {t('withdraw', 'Withdraw')}
          </button>
        </div>
      </div>
    );
  }

  renderStat(type, tone, Icon, label, value) {
    const active = this.state.list.trans_type === type;

    return (
      <button
        type="button"
        className={`wlt-stat wlt-stat--clickable ${active ? 'is-active' : ''}`}
        onClick={() => this.handleStatClick(type)}
        aria-pressed={active}
        title={
          active
            ? t('clear_filter', 'Click to clear this filter')
            : `${t('show_only', 'Show only')} ${label}`
        }
      >
        <span className={`wlt-stat__icon wlt-tone--${tone}`}>
          <Icon size={16} />
        </span>
        <span className="wlt-stat__body">
          <span className="wlt-stat__label">{label}</span>
          <span className="wlt-stat__value">
            <span className="wlt-num">{fmt(value)}</span>
            <small>{t('omr', 'OMR')}</small>
          </span>
        </span>
      </button>
    );
  }

  // "Showing Deposit transactions … Clear" banner under the toolbar.
  renderActiveFilterBar() {
    if (!this.hasAnyFilter()) return null;

    const { list } = this.state;
    const extra = this.activeFilterCount();

    return (
      <div className="wlt-filterbar" role="status">
        <Filter size={15} />
        <span className="wlt-filterbar__text">
          {t('showing', 'Showing')}{' '}
          <strong>
            {list.trans_type ? this.typeFilterLabel(list.trans_type) : t('all', 'All')}
          </strong>{' '}
          {t('transactions', 'transactions')}
          {list.search && (
            <>
              {' '}
              {t('matching', 'matching')} <strong>“{list.search}”</strong>
            </>
          )}
          {extra > 0 && (
            <span className="wlt-filterbar__more">
              {' '}
              · {extra}{' '}
              {extra === 1 ? t('more_filter', 'more filter') : t('more_filters', 'more filters')}
            </span>
          )}
        </span>
        <button type="button" className="wlt-filterbar__clear" onClick={this.clearFilters}>
          {t('clear', 'Clear')}
        </button>
      </div>
    );
  }

  renderFilters() {
    const { list, clients, groups, show_filters } = this.state;
    if (!show_filters) return null;

    return (
      <div className="wlt-filters">
        <div className="wlt-field">
          <label>{t('from_date', 'From date')}</label>
          <input
            type="date"
            className="wlt-input"
            name="fdate"
            value={list.fdate || ''}
            onChange={this.handleSearchChange}
          />
        </div>
        <div className="wlt-field">
          <label>{t('to_date', 'To date')}</label>
          <input
            type="date"
            className="wlt-input"
            name="tdate"
            value={list.tdate || ''}
            onChange={this.handleSearchChange}
          />
        </div>
        <div className="wlt-field">
          <label>{t('pay_type', 'Pay type')}</label>
          <select
            className="wlt-input"
            name="pay_type"
            value={list.pay_type || ''}
            onChange={this.handleSearchChange}
          >
            <option value="">{t('all', 'All')}</option>
            <option value="online">{t('online', 'Online')}</option>
            <option value="offline">{t('offline', 'Offline')}</option>
            <option value="wallet">{t('wallet', 'Wallet')}</option>
          </select>
        </div>
        <div className="wlt-field">
          <label>{t('client', 'Client')}</label>
          <select
            className="wlt-input"
            name="client"
            value={list.client || ''}
            onChange={this.handleSearchChange}
          >
            <option value="">{t('select_client', 'All clients')}</option>
            {clients.map((item) => (
              <option value={item.id} key={item.id}>
                {item.organization_name}
              </option>
            ))}
          </select>
        </div>
        <div className="wlt-field">
          <label>{t('group', 'Group')}</label>
          <select
            className="wlt-input"
            name="group"
            value={list.group || ''}
            onChange={this.handleSearchChange}
          >
            <option value="">{t('select_group', 'All groups')}</option>
            {groups.map((item) => (
              <option value={item.id} key={item.id}>
                {item.group_name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="wlt-btn wlt-btn--ghost wlt-btn--sm"
          onClick={this.clearFilters}
        >
          <X size={14} />
          {t('clear', 'Clear')}
        </button>
      </div>
    );
  }

  renderRow(row) {
    const { Icon, tone } = typeVisual(row);
    const credit = toNumber(row.credit);
    const debit = toNumber(row.debit);

    return (
      <tr key={row.id}>
        <td>
          <div className="wlt-tx">
            <span className={`wlt-tx__icon wlt-tone--${tone}`}>
              <Icon size={16} />
            </span>
            <div className="wlt-tx__text">
              <span className="wlt-tx__id wlt-num">{row.transaction_id || '—'}</span>
              <span className="wlt-tx__meta">
                #{row.id}
                {row.reference ? ` · Ref ${row.reference}` : ''}
              </span>
            </div>
          </div>
        </td>
        <td className="is-num">
          {credit ? (
            <span className="wlt-num wlt-credit">+{fmt(credit)}</span>
          ) : (
            <span className="wlt-dash">—</span>
          )}
        </td>
        <td className="is-num">
          {debit ? (
            <span className="wlt-num wlt-debit">-{fmt(debit)}</span>
          ) : (
            <span className="wlt-dash">—</span>
          )}
        </td>
        <td className="wlt-type">{typeLabel(row)}</td>
        <td>
          <span className={`wlt-pill wlt-pill--${statusTone(row)}`}>{statusLabel(row)}</span>
        </td>
        <td className="is-num wlt-num wlt-date">{formatDate(row.created_at)}</td>
      </tr>
    );
  }

  renderPagination() {
    const { list, totalRows } = this.state;
    const totalPages = this.getTotalPages();
    const from = totalRows ? (list.page - 1) * list.limit + 1 : 0;
    const to = Math.min(list.page * list.limit, totalRows);

    return (
      <div className="wlt-pager">
        <label className="wlt-pager__size">
          {t('rows_per_page', 'Rows per page')}
          <select
            className="wlt-input wlt-input--sm"
            value={list.limit}
            onChange={this.handleLimitChange}
          >
            {ROWS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <span className="wlt-pager__range wlt-num">
          {from}-{to} {t('of', 'of')} {totalRows}
        </span>

        <div className="wlt-pager__nav">
          <button
            type="button"
            disabled={list.page <= 1}
            onClick={() => this.goToPage(1)}
            aria-label="First page"
          >
            <ChevronsLeft size={15} />
          </button>
          <button
            type="button"
            disabled={list.page <= 1}
            onClick={() => this.goToPage(list.page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            type="button"
            disabled={list.page >= totalPages}
            onClick={() => this.goToPage(list.page + 1)}
            aria-label="Next page"
          >
            <ChevronRight size={15} />
          </button>
          <button
            type="button"
            disabled={list.page >= totalPages}
            onClick={() => this.goToPage(totalPages)}
            aria-label="Last page"
          >
            <ChevronsRight size={15} />
          </button>
        </div>
      </div>
    );
  }

  renderTransactions() {
    const { transactions, totalRows, is_loading, list, show_filters, exporting } = this.state;
    const filterCount = this.activeFilterCount();

    return (
      <div className="wlt-card">
        <div className="wlt-card__head">
          <div>
            <h3 className="wlt-card__title">{t('transaction_summary', 'Transaction Summary')}</h3>
            <span className="wlt-card__sub">
              {totalRows} {t('transactions', 'transactions')}
              {this.hasAnyFilter() && ` · ${t('filtered', 'filtered')}`}
            </span>
          </div>

          <div className="wlt-toolbar">
            <div className="wlt-search">
              <Search size={16} />
              <input
                type="text"
                name="search"
                placeholder={t('search_transactions', 'Search ID, transaction or reference')}
                value={list.search || ''}
                onChange={this.handleSearchChange}
              />
            </div>
            <select
              className="wlt-input wlt-input--type"
              name="trans_type"
              value={list.trans_type || ''}
              onChange={this.handleSearchChange}
            >
              <option value="">{t('all_types', 'All Types')}</option>
              {Object.keys(TYPE_FILTERS).map((type) => (
                <option key={type} value={type}>
                  {this.typeFilterLabel(type)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`wlt-btn wlt-btn--ghost ${show_filters || filterCount ? 'is-active' : ''}`}
              onClick={() => this.setState({ show_filters: !show_filters })}
            >
              <SlidersHorizontal size={15} />
              {t('filters', 'Filters')}
              {filterCount > 0 && <span className="wlt-badge">{filterCount}</span>}
            </button>
            <button
              type="button"
              className="wlt-btn wlt-btn--ghost"
              onClick={this.handleExport}
              disabled={exporting || !totalRows}
            >
              <Download size={15} />
              {exporting ? t('exporting', 'Exporting…') : t('export', 'Export')}
            </button>
          </div>
        </div>

        {this.renderActiveFilterBar()}
        {this.renderFilters()}

        <div className={`wlt-table-wrap ${is_loading ? 'is-loading' : ''}`}>
          <table className="wlt-table">
            <thead>
              <tr>
                <th>{t('transaction', 'Transaction')}</th>
                <th className="is-num">{t('credit', 'Credit')}</th>
                <th className="is-num">{t('debit', 'Debit')}</th>
                <th>{t('type', 'Type')}</th>
                <th>{t('status', 'Status')}</th>
                <th className="is-num">{t('date', 'Date')}</th>
              </tr>
            </thead>
            <tbody>
              {is_loading &&
                !transactions.length &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="wlt-skeleton-row">
                    <td colSpan={6}>
                      <span className="wlt-skeleton" />
                    </td>
                  </tr>
                ))}
              {!is_loading && !transactions.length && (
                <tr>
                  <td colSpan={6} className="wlt-empty">
                    <WalletIcon size={28} />
                    <span>{t('no_transactions', 'No transactions found')}</span>
                  </td>
                </tr>
              )}
              {transactions.map((row) => this.renderRow(row))}
            </tbody>
          </table>
          {is_loading && transactions.length > 0 && (
            <div className="wlt-table-overlay">
              <span className="wlt-spinner" />
            </div>
          )}
        </div>

        {this.renderPagination()}
      </div>
    );
  }

  renderWithdrawModal() {
    const { withdraw_popup, input, errors, user_info, is_wthdraw_req_sending } = this.state;
    const remaining = toNumber(user_info.wallet_amount) - toNumber(input.amount);

    const field = (name, label, props = {}) => (
      <div className="wlt-field">
        <label htmlFor={`wlt-${name}`}>
          {label} <span className="wlt-req">*</span>
        </label>
        <input
          id={`wlt-${name}`}
          type="text"
          className={`wlt-input ${errors[name] ? 'has-error' : ''}`}
          name={name}
          placeholder={label}
          value={input[name] || ''}
          onChange={this.handleChange}
          {...props}
        />
        {errors[name] && <small className="wlt-error">{errors[name]}</small>}
      </div>
    );

    return (
      <Modal show={withdraw_popup} onHide={this.closeWithdraw} centered dialogClassName="wlt-modal">
        <form onSubmit={this.handleSubmit} autoComplete="off" className="wlt-modal__body">
          <div className="wlt-modal__head">
            <span className="wlt-stat__icon wlt-tone--blue">
              <ArrowUpRight size={18} />
            </span>
            <div>
              <h4>{t('withdraw', 'Withdraw')}</h4>
              <p>{t('withdraw_display_message')}</p>
            </div>
            <button
              type="button"
              className="wlt-modal__close"
              onClick={this.closeWithdraw}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="wlt-modal__balance">
            <span>{t('available_balance', 'Available Balance')}</span>
            <strong className="wlt-num">
              {fmt(user_info.wallet_amount)} <small>{t('omr', 'OMR')}</small>
            </strong>
          </div>

          {field('bank', t('bank', 'Bank'))}
          {field('beneficiary', t('beneficiary', 'Beneficiary'))}
          {field('account_number', t('account_number', 'Account Number'), { inputMode: 'numeric' })}

          <div className="wlt-field">
            <label htmlFor="wlt-amount">
              {t('withdraw', 'Withdraw')} {t('amount', 'Amount')} <span className="wlt-req">*</span>
            </label>
            <div className="wlt-amount">
              <input
                id="wlt-amount"
                type="text"
                inputMode="decimal"
                className={`wlt-input wlt-num ${errors.amount ? 'has-error' : ''}`}
                name="amount"
                placeholder="0.00"
                value={input.amount || ''}
                onChange={this.handleChange}
              />
              <button type="button" className="wlt-amount__max" onClick={this.setMaxAmount}>
                {t('max', 'Max')}
              </button>
            </div>
            {errors.amount && <small className="wlt-error">{errors.amount}</small>}
          </div>

          <div className="wlt-modal__remaining">
            <span>{t('balance_after', 'Balance after withdrawal')}</span>
            <strong className="wlt-num">
              {fmt(Math.max(0, remaining))} {t('omr', 'OMR')}
            </strong>
          </div>

          <div className="wlt-modal__actions">
            <button
              type="button"
              className="wlt-btn wlt-btn--ghost"
              onClick={this.closeWithdraw}
              disabled={is_wthdraw_req_sending}
            >
              {t('cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="wlt-btn wlt-btn--primary"
              disabled={is_wthdraw_req_sending}
            >
              {is_wthdraw_req_sending ? (
                <>
                  <span className="wlt-spinner wlt-spinner--light" />
                  {t('loading', 'Loading…')}
                </>
              ) : (
                <>
                  <ArrowUpRight size={16} />
                  {t('submit', 'Submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  render() {
    const { hideSidebar, credit_sum, client_due, hold_sum, debit_sum } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner title={t('my_wallet', 'My Wallet')} />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              {/* Sidebar: original position and grid classes */}
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>

              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                {/* Wallet-only content wrapper (new class names) */}
                <div className="wlt-page">
                  <div className="wlt-page__bar">
                    <div className="wlt-page__heading">
                      <span className="wlt-page__heading-icon">
                        <WalletIcon size={18} />
                      </span>
                      <div>
                        <h2>{t('my_wallet', 'My Wallet')}</h2>
                        <p>
                          {t(
                            'wallet_subtitle',
                            'Balance, holds and every transaction in one place'
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="wlt-page__toggle"
                      onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      title={hideSidebar ? t('show_menu', 'Show menu') : t('expand', 'Expand')}
                    >
                      {hideSidebar ? <Shrink size={16} /> : <Expand size={16} />}
                      <span>
                        {hideSidebar ? t('show_menu', 'Show menu') : t('expand', 'Expand')}
                      </span>
                    </button>
                  </div>

                  <div className="wlt">
                    <div className="wlt-top">
                      {this.renderBalanceCard()}
                      <div className="wlt-stats">
                        {this.renderStat(
                          'deposit',
                          'green',
                          ArrowDownLeft,
                          t('deposit', 'Deposit'),
                          credit_sum
                        )}
                        {this.renderStat(
                          'client_due',
                          'amber',
                          Building2,
                          t('client_due', 'Client Due'),
                          client_due
                        )}
                        {this.renderStat('hold', 'red', Lock, t('on_hold', 'On Hold'), hold_sum)}
                        {this.renderStat(
                          'withdraw',
                          'blue',
                          ArrowUpRight,
                          t('withdraw', 'Withdraw'),
                          debit_sum
                        )}
                      </div>
                    </div>
                    {this.renderTransactions()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {this.renderWithdrawModal()}
      </React.Fragment>
    );
  }
}
